import alt from 'components/Dispatcher';
import immutableStore from 'utils/immutableStore';
import InProgressReviewActions from 'actions/coaching/InProgressReviewActions';
import {Record, Map} from 'immutable';
import InProgressReview from 'records/InProgressReview';
import {Store} from '@store/@types/alt';

const makeKey = userId => `ev.reviews-in-progress|${userId}`;

type StringReviewId = string;

class InProgressReviewStoreState extends Record({
  inProgressReviews: Map(),
}) {
  readonly inProgressReviews: Map<StringReviewId, InProgressReview>;
}

@immutableStore
class InProgressReviewStore extends Store<InProgressReviewStoreState> {
  constructor() {
    super();
    this.state = new InProgressReviewStoreState();
    this.bindActions(InProgressReviewActions);
  }

  initialize(user) {
    // load from local storage
    const allFromLocalStorage = JSON.parse(localStorage.getItem(makeKey(user.id)) ?? '{}');

    if (!allFromLocalStorage) return Map();

    // re-convert into our Map of items
    const asImmutable = Map(
      Object.entries(allFromLocalStorage).map(([k, v]) => {
        return [k, new InProgressReview(v as Partial<InProgressReview>)];
      })
    ) as Map<StringReviewId, InProgressReview>;

    // sync with store
    this.setState({
      inProgressReviews: asImmutable,
    });
  }

  setInProgressReview({inProgressReview, user}) {
    // load from local storage
    const allFromLocalStorage = JSON.parse(localStorage.getItem(makeKey(user.id)) ?? '{}');

    // re-convert into our Map of items
    const asImmutable: Map<StringReviewId, InProgressReview> = allFromLocalStorage
      ? Map(
          Object.entries(allFromLocalStorage).map(([k, v]) => {
            return [k, new InProgressReview(v as Partial<InProgressReview>)];
          })
        )
      : Map();

    // add our current in progress review
    const withCurrentSnapshot = asImmutable.set(inProgressReview.id + '', inProgressReview);

    // serialize and save to local storage
    localStorage.setItem(makeKey(user.id), JSON.stringify(withCurrentSnapshot.toJS()));

    // keep store state in sync as well
    this.setState({
      inProgressReviews: withCurrentSnapshot,
    });
  }

  deleteInProgressReview({reviewId, user}) {
    // load from local storage
    const allFromLocalStorage = JSON.parse(localStorage.getItem(makeKey(user.id!)) ?? '{}');

    // re-convert into our Map of items
    const asImmutable: Map<StringReviewId, InProgressReview> = allFromLocalStorage
      ? Map(
          Object.entries(allFromLocalStorage).map(([k, v]) => {
            return [k, new InProgressReview(v as Partial<InProgressReview>)];
          })
        )
      : Map();

    // remove our current in progress review
    const withoutReviewId = asImmutable.remove(reviewId + '');

    // serialize and save to local storage
    localStorage.setItem(makeKey(user.id), JSON.stringify(withoutReviewId.toJS()));

    // keep store state in sync as well
    this.setState({
      inProgressReviews: withoutReviewId,
    });
  }
}

export default alt.createStore(InProgressReviewStore);
